import React, { useState, useMemo } from 'react';
import { Tabs, theme, Result } from 'antd';
import { 
    FolderOutlined, 
    PrinterOutlined, 
    MessageOutlined,
    AppstoreOutlined,
    TableOutlined 
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { hasPermission, categoryPermissions } from '../../utils/permissions';

import CategorySettings from './productCategories/components/CategorySettings';
import PrinterSettings from './printerCategories/components/PrinterSettings';
import AutoMessageSettings from './autoMessages/components/AutoMessageSettings';
import MappingSettings from './mappingSettings/components/MappingSettings';
import './CategoryManagement.css';

const { TabPane } = Tabs;

const CategoryManagement = () => {
    const [activeKey, setActiveKey] = useState('product-categories');
    const { user } = useSelector(state => state.auth);
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    // Check permissions for each tab
    const canViewCategories = categoryPermissions.canRead(user);
    const canManageProductCategories = categoryPermissions.canManageProductCategories(user);
    const canManagePrinterCategories = categoryPermissions.canManagePrinterCategories(user);
    const canManageAutoMessages = categoryPermissions.canManageAutoMessages(user);
    const canManageMappingSettings = categoryPermissions.canManageMappingSettings(user);

    // Define all possible tabs with their permission requirements
    const allTabs = [
        {
            key: 'product-categories',
            tab: <span><FolderOutlined /> Danh mục sản phẩm</span>,
            component: <CategorySettings user={user} />,
            permission: canManageProductCategories
        },
        {
            key: 'printer-categories',
            tab: <span><PrinterOutlined /> Danh mục nhà in</span>,
            component: <PrinterSettings user={user} />,
            permission: canManagePrinterCategories
        },
        {
            key: 'auto-messages',
            tab: <span><MessageOutlined /> Auto messages</span>,
            component: <AutoMessageSettings user={user} />,
            permission: canManageAutoMessages
        },
        {
            key: 'mapping-settings',
            tab: <span><TableOutlined /> Mapping sheet</span>,
            component: <MappingSettings user={user} />,
            permission: canManageMappingSettings
        }
    ];

    // Filter tabs based on permissions
    const availableTabs = useMemo(() => {
        return allTabs.filter(tab => tab.permission);
    }, [canManageProductCategories, canManagePrinterCategories, canManageAutoMessages, canManageMappingSettings]);

    // Set default active key to first available tab
    const defaultActiveKey = useMemo(() => {
        if (availableTabs.length > 0) {
            // If current active key is not available, switch to first available
            if (!availableTabs.find(tab => tab.key === activeKey)) {
                return availableTabs[0].key;
            }
        }
        return activeKey;
    }, [availableTabs, activeKey]);

    // Update active key if it changed due to permission filtering
    React.useEffect(() => {
        if (defaultActiveKey !== activeKey) {
            setActiveKey(defaultActiveKey);
        }
    }, [defaultActiveKey, activeKey]);

    // If user can't view any categories, show access denied
    if (!canViewCategories) {
        return (
            <div style={{ margin: '24px 16px' }}>
                <Result
                    status="403"
                    title="403"
                    subTitle="Xin lỗi, bạn không có quyền truy cập vào quản lý danh mục."
                />
            </div>
        );
    }

    // If no tabs are available, show limited access message
    if (availableTabs.length === 0) {
        return (
            <div style={{ margin: '24px 16px' }}>
                <Result
                    status="warning"
                    title="Quyền truy cập hạn chế"
                    subTitle="Bạn có thể xem danh mục nhưng không có quyền quản lý các danh mục cụ thể."
                />
            </div>
        );
    }

    // Removed useEffect that called fetchProductCategories() to avoid duplicate API calls
    // CategorySettings component will handle its own data fetching

    const handleTabChange = (key) => {
        setActiveKey(key);
    };

    return (
        <div
            style={{
                margin: '24px 16px',
                borderRadius: borderRadiusLG,
                minHeight: 280,
            }}
        >
            <Tabs
                activeKey={activeKey}
                onChange={handleTabChange}
                type="card"
                tabBarGutter={5}
                className="category-management-tabs"
            >
                {availableTabs.map(tab => (
                    <TabPane
                        tab={tab.tab}
                        key={tab.key}
                    >
                        {tab.component}
                    </TabPane>
                ))}
            </Tabs>
        </div>
    );
};

export default CategoryManagement;